import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X, RefreshCw } from "lucide-react";
import { useAdmin } from "./AdminContext";

// Shows check-in attempts that FAILED on the guest tablet (last 7 days).
// Failures are logged on the tablet (even while offline) and synced to the
// server when connection returns — so you can see who couldn't check in & why.
export function FailedCheckinsCard() {
  const { API } = useAdmin();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/failed-checkins?days=7`);
      setLogs(res.data || []);
    } catch (e) {
      console.error("Failed to load failed check-ins:", e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleDismiss = async (logId) => {
    try {
      await axios.delete(`${API}/admin/failed-checkins/${logId}`);
      setLogs((prev) => prev.filter((l) => l.id !== logId));
    } catch (e) {
      toast.error("Failed to dismiss entry");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Clear all failed check-in entries?")) return;
    try {
      await axios.delete(`${API}/admin/failed-checkins/all`);
      setLogs([]);
      toast.success("Failed check-in log cleared");
    } catch (e) {
      toast.error("Failed to clear log");
    }
  };

  if (logs.length === 0) return null;

  return (
    <Card className="bg-vault-surface border-amber-500/40 mb-8" data-testid="failed-checkins-card">
      <CardHeader className="border-b border-vault-border py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-outfit text-lg text-vault-text flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Failed Check-In Attempts
            <span className="text-xs font-mono text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded" data-testid="failed-checkins-count">
              {logs.length} in last 7 days
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={fetchLogs} disabled={loading} className="text-vault-text-secondary hover:text-vault-gold h-8 w-8 p-0" title="Refresh" data-testid="failed-checkins-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleClearAll} className="text-red-400 hover:text-red-300 hover:bg-red-900/30 text-xs" data-testid="failed-checkins-clear-all-btn">
              Clear all
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <p className="text-xs text-vault-text-secondary mb-3">
          These guests tried to check in on the tablet but were blocked. Logged on the tablet even when offline, synced once connection returned.
        </p>
        <div className="max-h-64 overflow-y-auto space-y-2">
          {logs.map((log) => (
            <div key={log.id} className="flex items-center justify-between p-3 bg-amber-900/10 rounded border border-amber-500/20" data-testid={`failed-checkin-row-${log.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-vault-text font-medium text-sm">
                  {log.employee_name || "Unknown"}
                  {log.employee_number && <span className="text-vault-text-secondary font-mono ml-2">#{log.employee_number}</span>}
                  {log.room_number && <span className="text-vault-text-secondary ml-2">Room {log.room_number}</span>}
                </p>
                <p className="text-xs text-amber-400 mt-0.5">{log.reason}{log.detail ? ` — ${log.detail}` : ""}</p>
                <p className="text-xs text-vault-text-secondary mt-0.5 font-mono">
                  {log.attempted_at ? new Date(log.attempted_at).toLocaleString() : "Unknown time"}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDismiss(log.id)} className="text-red-400 hover:text-red-300 hover:bg-red-900/30 h-8 w-8 p-0 shrink-0" title="Dismiss" data-testid={`dismiss-failed-checkin-${log.id}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
